import { SearchClient } from 'instantsearch.js';
import getConfig from 'next/config';
import { algoliaClient } from './client';

// Import any configuration values we might need
const { publicRuntimeConfig } = getConfig() || {};

// Development helpers for testing fallback scenarios
const DEV_HELPERS = {
    forceProxyFailure: false,
};

// Only expose in development and client-side
if (process.env.NODE_ENV === 'development' && typeof window !== 'undefined') {
    (window as any).__SEARCH_CLIENT_TEST_HELPERS__ = {
        enableProxyFailure: () => {
            DEV_HELPERS.forceProxyFailure = true;
            console.log('Proxy failure mode enabled - searches will fall back to direct Algolia');
        },
        disableProxyFailure: () => {
            DEV_HELPERS.forceProxyFailure = false;
            console.log('Proxy failure mode disabled - normal operation resumed');
        },
        status: () => {
            console.log('Current test helper status:', {
                proxyFailure: DEV_HELPERS.forceProxyFailure,
            });
        },
    };
}

// Cache configuration
const BASE_CACHE_TTL = 60_000; // 1 minute base TTL
const MAX_CACHE_TTL = 600_000; // 10 minutes maximum TTL
const MIN_CACHE_TTL = 30_000; // 30 seconds minimum TTL
const MAX_CACHE_SIZE = 50; // Reduced from 100 to accommodate larger page sizes
const MAX_STORAGE_SIZE = 50 * 1024 * 1024; // 50MB max storage size
const CACHE_KEY_PREFIX = 'algolia_cache_';

// Track query statistics for adaptive TTL
interface QueryStats {
    hitCount: number;
    lastAccessed: number;
    averageResponseTime: number;
    errorCount: number;
}

const queryStats = new Map<string, QueryStats>();

// Calculate adaptive TTL based on query patterns
function getAdaptiveTTL(queryKey: string): number {
    const stats = queryStats.get(queryKey);
    if (!stats) return BASE_CACHE_TTL;

    // Increase TTL for frequently accessed queries
    const frequencyMultiplier = Math.min(stats.hitCount / 10, 2); // Max 2x multiplier

    // Reduce TTL if there are errors
    const errorPenalty = Math.max(1 - stats.errorCount / 10, 0.5); // Max 50% reduction

    // Calculate final TTL
    const adaptiveTTL = BASE_CACHE_TTL * frequencyMultiplier * errorPenalty;

    // Ensure TTL stays within bounds
    return Math.min(Math.max(adaptiveTTL, MIN_CACHE_TTL), MAX_CACHE_TTL);
}

// Update query statistics
function updateQueryStats(queryKey: string, responseTime: number, isError: boolean = false) {
    const now = Date.now();
    const stats = queryStats.get(queryKey) || {
        hitCount: 0,
        lastAccessed: now,
        averageResponseTime: responseTime,
        errorCount: 0,
    };

    stats.hitCount++;
    stats.lastAccessed = now;
    stats.averageResponseTime =
        (stats.averageResponseTime * (stats.hitCount - 1) + responseTime) / stats.hitCount;
    if (isError) stats.errorCount++;

    queryStats.set(queryKey, stats);
}

// Check if we're running on the server
const isServer = typeof window === 'undefined';

// Enhanced request/response cache with adaptive TTL using sessionStorage
const searchCache = {
    get(key: string) {
        if (isServer) return null;
        try {
            const item = sessionStorage.getItem(CACHE_KEY_PREFIX + key);
            if (!item) return null;

            const { data, timestamp, ttl } = JSON.parse(item);
            const age = Date.now() - timestamp;

            // Use stored TTL or fallback to adaptive TTL
            const effectiveTTL = ttl || getAdaptiveTTL(key);

            if (age > effectiveTTL) {
                sessionStorage.removeItem(CACHE_KEY_PREFIX + key);
                return null;
            }

            // Update stats on cache hit
            updateQueryStats(key, 0);
            return { data, timestamp, ttl: effectiveTTL };
        } catch {
            return null;
        }
    },
    set(key: string, value: any, responseTime: number = 0) {
        if (isServer) return;
        try {
            const ttl = getAdaptiveTTL(key);
            const cacheEntry = {
                data: value,
                timestamp: Date.now(),
                ttl,
            };

            sessionStorage.setItem(CACHE_KEY_PREFIX + key, JSON.stringify(cacheEntry));

            // Update query statistics
            updateQueryStats(key, responseTime);
        } catch {
            // If storage is full, clear old entries
            this.cleanup();
            try {
                const ttl = getAdaptiveTTL(key);
                sessionStorage.setItem(
                    CACHE_KEY_PREFIX + key,
                    JSON.stringify({
                        data: value,
                        timestamp: Date.now(),
                        ttl,
                    }),
                );
                updateQueryStats(key, responseTime);
            } catch {
                // If still failing, just ignore
            }
        }
    },
    delete(key: string) {
        if (isServer) return;
        sessionStorage.removeItem(CACHE_KEY_PREFIX + key);
    },
    cleanup() {
        if (isServer) return;
        const now = Date.now();
        const keys = [];
        let totalSize = 0;

        // Collect all cache keys, timestamps, and calculate total size
        for (let i = 0; i < sessionStorage.length; i++) {
            const key = sessionStorage.key(i);
            if (key?.startsWith(CACHE_KEY_PREFIX)) {
                try {
                    const item = sessionStorage.getItem(key) || '';
                    const parsed = JSON.parse(item);
                    totalSize += item.length * 2; // UTF-16 uses 2 bytes per character
                    keys.push({
                        key,
                        timestamp: parsed.timestamp,
                        size: item.length * 2,
                    });
                } catch {
                    sessionStorage.removeItem(key);
                }
            }
        }

        // Remove expired entries
        keys.forEach(({ key, timestamp }) => {
            const ttl = getAdaptiveTTL(key);
            if (now - timestamp > ttl) {
                sessionStorage.removeItem(key);
            }
        });

        // If over size limit or too many entries, remove oldest ones until within limits
        if (totalSize > MAX_STORAGE_SIZE || keys.length > MAX_CACHE_SIZE) {
            keys.sort((a, b) => a.timestamp - b.timestamp);

            let currentSize = totalSize;
            let currentCount = keys.length;

            for (const { key, size } of keys) {
                if (currentSize <= MAX_STORAGE_SIZE && currentCount <= MAX_CACHE_SIZE) {
                    break;
                }
                sessionStorage.removeItem(key);
                currentSize -= size;
                currentCount--;
            }
        }
    },
};

// Basic query normalization for tracking
interface NormalizedQuery {
    indexName: string;
    page: number;
}

function normalizeQuery(query: any): NormalizedQuery {
    return {
        indexName: query.indexName,
        page: parseInt(new URLSearchParams(query.params).get('page') || '0', 10),
    };
}

// Enhanced request tracking
interface PendingRequest {
    promise: Promise<unknown>;
    normalizedQuery: NormalizedQuery;
    timestamp: number;
}

const pendingRequests = new Map<string, PendingRequest>();

// Custom function to transform params in InstantSearch format to the format expected by the dropin Algolia Proxy API
// This is the same transformation logic from the API route
function transformParams(params: any): string {
    // Can't find a more suitable type for this yet
    if (typeof params === 'string') {
        return params;
    }

    const transformed: Record<string, any> = { ...params };

    // Convert arrays to JSON strings
    if (Array.isArray(transformed.facets)) {
        transformed.facets = JSON.stringify(transformed.facets);
    }

    if (Array.isArray(transformed.ruleContexts)) {
        transformed.ruleContexts = JSON.stringify(transformed.ruleContexts);
    }

    if (transformed.facetFilters) {
        if (Array.isArray(transformed.facetFilters)) {
            transformed.facetFilters = JSON.stringify(transformed.facetFilters);
        }
    }

    // Convert the object to a query string with proper encoding
    return Object.entries(transformed)
        .map(([key, value]) => {
            if (value === undefined || value === null || value === '') {
                return `${encodeURIComponent(key)}=`;
            }
            return `${encodeURIComponent(key)}=${encodeURIComponent(value)}`;
        })
        .join('&');
}

// Server-side direct API call function
async function callDropinApiDirectly(queries: readonly any[]) {
    // Transform requests to match expected format
    const transformedRequests = queries.map((request) => ({
        ...request,
        params:
            typeof request.params === 'object' ? transformParams(request.params) : request.params,
    }));

    const url = new URL(`/v1/Search/GetResults`, process.env.DROPPOINTS_API);

    const result = await fetch(url.href, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'Ocp-Apim-Subscription-Key': `${process.env.API_SECURITY_VALUE}`,
        },
        body: JSON.stringify({ requests: transformedRequests }),
    });

    if (!result.ok) {
        throw new Error(`Dropin API error: ${result.status}`);
    }

    return await result.json();
}

// Client-side API route call function
async function callProxyApiRoute(queries: readonly any[]) {
    // For testing: force proxy failure in development
    if (process.env.NODE_ENV === 'development' && DEV_HELPERS.forceProxyFailure) {
        throw new Error('Forced proxy failure for testing');
    }

    const result = await fetch('/api/search/algolia-proxy', {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'Ocp-Apim-Subscription-Key': `${publicRuntimeConfig?.API_SECURITY_VALUE}`,
        },
        body: JSON.stringify({ requests: queries }),
    });

    if (!result.ok) {
        throw new Error(`API error: ${result.status}`);
    }

    return await result.json();
}

// Create the client that implements the SearchClient interface
const client: SearchClient = {
    /**
     * Main search method required by InstantSearch
     */
    search(queries: readonly any[]) {
        // Create cache key from the request parameters
        const cacheKey = JSON.stringify(queries);

        // Check if we have a cached response
        const cachedEntry = searchCache.get(cacheKey);
        if (cachedEntry && Date.now() - cachedEntry.timestamp < cachedEntry.ttl) {
            return Promise.resolve(cachedEntry.data);
        }

        // Check if we have a pending request for this same query
        if (pendingRequests.has(cacheKey)) {
            return pendingRequests.get(cacheKey)!.promise;
        }

        // Choose the appropriate API call method based on environment
        const apiCall = isServer
            ? () => callDropinApiDirectly(queries)
            : () => callProxyApiRoute(queries);

        // No cache hit, perform the request
        const requestStartTime = Date.now();
        const promise = apiCall()
            .then((data) => {
                const responseTime = Date.now() - requestStartTime;

                // Cache the successful response with response time for statistics
                searchCache.set(cacheKey, data, responseTime);
                // Remove from pending requests
                pendingRequests.delete(cacheKey);

                // Clean up expired entries occasionally
                if (Math.random() < 0.1) {
                    searchCache.cleanup();
                }

                return data;
            })
            .catch(async (error) => {
                // Remove from pending requests on error
                pendingRequests.delete(cacheKey);

                // Log the proxy failure
                console.warn('Search proxy failed, falling back to direct Algolia:', error);

                try {
                    // Try direct Algolia as fallback
                    const fallbackResponse = await algoliaClient.search(queries);

                    // Mark this as a fallback response
                    if (fallbackResponse?.results) {
                        fallbackResponse.results = fallbackResponse.results.map((result) => ({
                            ...result,
                            _fromFallback: true, // Flag to indicate this came from fallback
                        }));
                    }

                    return fallbackResponse;
                } catch (fallbackError) {
                    console.error('Algolia fallback also failed:', fallbackError);

                    // If both proxy and direct Algolia fail, return empty response
                    return {
                        results: queries.map((request) => ({
                            hits: [],
                            nbHits: 0,
                            page: 0,
                            nbPages: 0,
                            hitsPerPage: 0,
                            processingTimeMS: 0,
                            exhaustiveNbHits: true,
                            query: '',
                            params: request.params,
                            index: request.indexName,
                            _fromFallback: false,
                            _error: true,
                        })),
                    };
                }
            });

        // Store the request
        pendingRequests.set(cacheKey, {
            promise,
            normalizedQuery: normalizeQuery(queries[0]),
            timestamp: Date.now(),
        });

        return promise;
    },

    /**
     * Method used for searchable facets - uses original Algolia client
     */
    searchForFacetValues(queries: readonly any[]) {
        // Use the original Algolia client for facet searches
        return algoliaClient.searchForFacetValues(queries);
    },
};

// Export the client as proxySearchClient
export const proxySearchClient = client;
